(() => {
  const AUTO_APPEND_LIMIT = 10;
  const AUTO_APPEND_ROOT_MARGIN_PX = 120;
  const SAMPLE_PARENT_SKUS = {
    samplePot: "CB-125",
    a5Swatch: "XC-045",
  };

  const SELECTORS = {
    sectionRoot: ".colour-planet",
    filters: ".colour-planet__filters",
    filterButton: ".colour-planet__filter-button",
    availableCount: "[data-colour-planet-available-count]",
    control: ".colour-planet__load-more-control",
    button: ".colour-planet__load-more-button",
    cardButton: ".colour-planet__card[data-modal]",
    group: ".colour-planet__group[data-filter-group]",
    groupRows: ".colour-planet__group-rows",
    groupRow: ".colour-planet__group-row",
    cardsList: ".colour-planet__cards",
    loaderItem: ".colour-planet__card-item--loader",
    sentinel: ".colour-planet__load-more-sentinel",
    loaderLabel: ".colour-planet__load-more-label",
    sharedModalHero: "[data-modal-hero]",
    sharedModalTitle: "[data-modal-title]",
    sharedModalSave: "[data-modal-save]",
    sharedModalDescription: "[data-modal-description]",
    sharedModalCode: "[data-modal-code]",
    sharedModalFandeck: "[data-modal-fandeck]",
    sharedModalCoats: "[data-modal-coats]",
    sharedModalUndercoat: "[data-modal-undercoat]",
    sharedModalRgb: "[data-modal-rgb]",
    sharedModalHex: "[data-modal-hex]",
    sharedModalLrv: "[data-modal-lrv]",
    sharedModalCollection: "[data-modal-collection]",
    sharedModalDownloadBim: "[data-modal-download-bim]",
    sharedModalDownloadCollectionBim: "[data-modal-download-collection-bim]",
    sharedModalDownloadFullBim: "[data-modal-download-full-bim]",
    sharedModalDownloadSwatch: "[data-modal-download-swatch]",
    sharedModalDownloadDroplet: "[data-modal-download-droplet]",
    sharedModalViewColour: "[data-modal-view-colour]",
    sharedModalColourStudio: "[data-modal-colour-studio]",
    sharedModalCreatePalette: "[data-modal-create-palette]",
    sampleCatalog: "[data-colour-planet-sample-catalog]",
    sharedModalAddA5Swatch: "[data-modal-add-a5-swatch]",
    sharedModalAddSamplePot: "[data-modal-add-sample-pot]",
    sharedModalA5SwatchNote: "[data-modal-a5-swatch-note]",
    sharedModalSamplePotNote: "[data-modal-sample-pot-note]",
    sharedModalSampleFeedback: "[data-modal-sample-feedback]",
  };

  function setTriggerExpandedState(trigger, isExpanded) {
    if (!trigger) {
      return;
    }

    trigger.setAttribute("aria-expanded", isExpanded ? "true" : "false");
  }

  function shouldFocusCloseButton(trigger) {
    if (!trigger) {
      return false;
    }

    const shouldFocusClose =
      trigger.dataset.colourPlanetInitialFocus === "close-button";
    delete trigger.dataset.colourPlanetInitialFocus;

    return shouldFocusClose;
  }

  function setModalTextContent(element, value, fallback = "Not available") {
    if (!element) {
      return;
    }

    const normalizedValue = typeof value === "string" ? value.trim() : "";
    element.textContent = normalizedValue || fallback;
  }

  // Hydrates the single <colour-save-button> living in the shared modal for
  // whichever colour was just opened, reusing that element's own instance
  // methods (syncPressedState/setPressed) rather than re-implementing the
  // saved/unsaved logic here. The heart stays hidden until a colour is
  // selected — see the "hidden" attribute on the element in colour-planet.liquid.
  function hydrateModalSaveButton(saveButton, trigger) {
    if (!saveButton) {
      return;
    }

    const productId = `${trigger.dataset.productId || ""}`.trim();
    if (!productId) {
      saveButton.hidden = true;
      return;
    }

    const colour = {
      productId,
      handle: `${trigger.dataset.productHandle || ""}`.trim(),
      name: `${trigger.dataset.productTitle || ""}`.trim(),
      hex: `${trigger.dataset.productHex || ""}`.trim(),
    };

    saveButton.dataset.productId = colour.productId;
    saveButton.dataset.productHandle = colour.handle;
    saveButton.dataset.productName = colour.name;
    saveButton.dataset.productHex = colour.hex;
    saveButton.productId = colour.productId;
    saveButton.colour = colour;
    saveButton.hidden = false;

    if (typeof saveButton.syncPressedState === "function") {
      saveButton.syncPressedState();
    }
  }

  function setOptionalModalTextContent(element, value) {
    if (!element) {
      return;
    }

    const normalizedValue = typeof value === "string" ? value.trim() : "";
    element.hidden = !normalizedValue;
    element.textContent = normalizedValue;
  }

  function getBimBaseOrigin(sectionRoot) {
    const configuredOrigin = `${sectionRoot?.dataset.storeOrigin || ""}`.trim();

    return configuredOrigin || window.location.origin;
  }

  function getBimPageUrl(sectionRoot) {
    const configuredPageUrl = `${sectionRoot?.dataset.bimPageUrl || ""}`.trim();
    const fallbackPageUrl = "/pages/bim-content";
    const pageUrl = configuredPageUrl || fallbackPageUrl;

    try {
      return new URL(pageUrl, window.location.origin);
    } catch (error) {
      return new URL(fallbackPageUrl, window.location.origin);
    }
  }

  function getBimStore(sectionRoot) {
    const configuredStore = `${sectionRoot?.dataset.bimStore || ""}`.trim();

    return configuredStore || "haymes-paint";
  }

  function getFullBimItemId(sectionRoot) {
    const configuredItemId = `${sectionRoot?.dataset.bimFullId || ""}`.trim();

    return configuredItemId || "haymes-paint-complete-library";
  }

  function buildBimUrl(item, sectionRoot) {
    const normalizedItem = `${item || ""}`.trim();

    if (!normalizedItem) {
      return "#";
    }

    const bimUrl = getBimPageUrl(sectionRoot);
    bimUrl.searchParams.set("store", getBimStore(sectionRoot));
    bimUrl.searchParams.set("item", normalizedItem);

    return bimUrl.toString();
  }

  function buildColourBimUrl(productTitle, sectionRoot) {
    const normalizedTitle = `${productTitle || ""}`
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");

    if (!normalizedTitle) {
      return "#";
    }

    return buildBimUrl(`paint-haymes-${normalizedTitle}`, sectionRoot);
  }

  function getExploreColourOrigin(sectionRoot) {
    const baseOrigin = getBimBaseOrigin(sectionRoot);

    try {
      const currentUrl = new URL("https://haymespaint.com.au");
      const normalizedHostname = currentUrl.hostname
        .replace(/^explore-colour\./, "")
        .replace(/^www\./, "");
      const baseHost = currentUrl.port
        ? `${normalizedHostname}:${currentUrl.port}`
        : normalizedHostname;

      return `${currentUrl.protocol}//explore-colour.${baseHost}`;
    } catch (error) {
      const fallbackHost = window.location.host
        .replace(/^explore-colour\./, "")
        .replace(/^www\./, "");

      return `https://explore-colour.${fallbackHost}`;
    }
  }

  function normalizeExploreColourName(value) {
    return `${value || ""}`.trim().toLowerCase().replace(/\s+/g, "");
  }

  function buildExploreColourSlug(productTitle, sku) {
    const normalizedTitle = normalizeExploreColourName(productTitle);
    const normalizedSku = `${sku || ""}`.trim();

    if (!normalizedTitle || !normalizedSku) {
      return "";
    }

    return `${normalizedTitle}-${normalizedSku}`;
  }

  function buildColourStudioUrl(productTitle, sku, sectionRoot) {
    const slug = buildExploreColourSlug(productTitle, sku);

    if (!slug) {
      return "#";
    }

    return `${getExploreColourOrigin(sectionRoot)}/colour/${encodeURIComponent(slug)}`;
  }

  function buildCreatePaletteUrl(productTitle, sku, sectionRoot) {
    const slug = buildExploreColourSlug(productTitle, sku);

    if (!slug) {
      return "#";
    }

    const url = new URL(`${getExploreColourOrigin(sectionRoot)}/combine`);
    url.searchParams.set("a", slug);

    return url.toString();
  }

  function setModalLinkAttributes(link, href) {
    if (!link) {
      return;
    }

    link.setAttribute("href", href || "#");
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  }

  function setOptionalExternalModalLinkAttributes(link, href) {
    if (!link) {
      return;
    }

    const normalizedHref = `${href || ""}`.trim();
    const isAvailable = normalizedHref && normalizedHref !== "#";

    link.setAttribute("href", isAvailable ? normalizedHref : "#");
    link.classList.toggle("is-unavailable", !isAvailable);
    link.setAttribute("aria-disabled", isAvailable ? "false" : "true");

    if (isAvailable) {
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
      link.removeAttribute("tabindex");
      return;
    }

    link.removeAttribute("target");
    link.removeAttribute("rel");
    link.setAttribute("tabindex", "-1");
  }

  function setActionEnabledState(button, enabled) {
    if (!button) {
      return;
    }

    button.disabled = !enabled;
    button.setAttribute("aria-disabled", enabled ? "false" : "true");
  }

  function normalizeRgbValue(value) {
    const normalizedValue = `${value || ""}`.trim();

    if (!normalizedValue) {
      return "";
    }

    if (/^rgba?\(/i.test(normalizedValue)) {
      return normalizedValue;
    }

    return normalizedValue.includes(",")
      ? `rgb(${normalizedValue})`
      : normalizedValue;
  }

  function normalizeHexValue(value) {
    const normalizedValue = `${value || ""}`.trim();

    if (!normalizedValue) {
      return "";
    }

    return normalizedValue.startsWith("#")
      ? normalizedValue
      : `#${normalizedValue}`;
  }

  function getDownloadBackgroundColor(trigger) {
    if (!trigger) {
      return "#ffffff";
    }

    const swatchValue = `${trigger.dataset.productSwatch || ""}`.trim();
    const rgbValue = normalizeRgbValue(trigger.dataset.productRgb);
    const hexValue = normalizeHexValue(trigger.dataset.productHex);

    return swatchValue || rgbValue || hexValue || "#ffffff";
  }

  function hexToRgb(hex) {
    const normalizedHex = normalizeHexValue(hex).replace("#", "");

    if (!normalizedHex || !/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(normalizedHex)) {
      return null;
    }

    const expandedHex =
      normalizedHex.length === 3
        ? normalizedHex
            .split("")
            .map((character) => `${character}${character}`)
            .join("")
        : normalizedHex;
    const bigint = Number.parseInt(expandedHex, 16);

    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  }

  function sanitizeFilenamePart(value, fallback) {
    const normalizedValue = `${value || ""}`
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    return normalizedValue || fallback;
  }

  function loadImage(source) {
    return new Promise((resolve, reject) => {
      if (!source) {
        reject(new Error("Image source is required"));
        return;
      }

      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = () => resolve(image);
      image.onerror = () =>
        reject(new Error(`Unable to load image: ${source}`));
      image.src = source;
    });
  }

  function getDownloadImageUrl(trigger, downloadType) {
    if (!trigger) {
      return "";
    }

    if (downloadType === "droplet") {
      return `${trigger.dataset.productDownloadDropletUrl || ""}`.trim();
    }

    return `${trigger.dataset.productDownloadSwatchUrl || ""}`.trim();
  }

  async function triggerColourPlanetDownload(
    sectionRoot,
    trigger,
    downloadType,
  ) {
    if (!trigger) {
      return;
    }

    const imageUrl = getDownloadImageUrl(trigger, downloadType);
    const backgroundColor = getDownloadBackgroundColor(trigger);
    const productName = `${trigger.dataset.productTitle || "Colour"}`.trim();
    const productSku =
      `${trigger.dataset.productOriginalSku || "colour"}`.trim();
    const fallbackCanvasSize = 1200;
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("Canvas rendering is unavailable");
    }

    try {
      const image = await loadImage(imageUrl);
      canvas.width = image.width;
      canvas.height = image.height;
      context.fillStyle = backgroundColor;
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
    } catch (error) {
      canvas.width = fallbackCanvasSize;
      canvas.height = fallbackCanvasSize;
      context.fillStyle = backgroundColor;
      context.fillRect(0, 0, canvas.width, canvas.height);
    }

    const brightnessSource = backgroundColor.startsWith("#")
      ? hexToRgb(backgroundColor)
      : null;
    const brightness = brightnessSource
      ? (brightnessSource.r * 299 +
          brightnessSource.g * 587 +
          brightnessSource.b * 114) /
        1000
      : 255;
    const chosenLogo =
      brightness < 128
        ? `${sectionRoot?.dataset.downloadLogoDark || ""}`.trim()
        : `${sectionRoot?.dataset.downloadLogoLight || ""}`.trim();
    const textColor = "#021D49";

    if (chosenLogo) {
      try {
        const logo = await loadImage(chosenLogo);
        const padding = 20;
        const logoWidth = 50;
        const logoHeight = (logo.height / logo.width) * logoWidth;
        const logoX = canvas.width - logoWidth - padding;
        const logoY = padding;

        context.drawImage(logo, logoX, logoY, logoWidth, logoHeight);
        context.font = "normal 14px Muller";
        context.fillStyle = textColor;
        context.textAlign = "right";
        context.fillText(
          productName,
          canvas.width - padding,
          logoY + logoHeight + 20,
        );
      } catch (error) {
        console.error("[Colour Planet] Failed to render download logo", error);
      }
    }

    const link = document.createElement("a");
    link.download = `${sanitizeFilenamePart(productName, "colour")}-${sanitizeFilenamePart(productSku, "sku")}-${downloadType}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  }

  function hydrateDownloadActions(sectionRoot, modal, trigger) {
    if (!modal || !trigger) {
      return;
    }

    const downloadSwatchButton = modal.querySelector(
      SELECTORS.sharedModalDownloadSwatch,
    );
    const downloadDropletButton = modal.querySelector(
      SELECTORS.sharedModalDownloadDroplet,
    );

    setActionEnabledState(
      downloadSwatchButton,
      Boolean(
        getDownloadImageUrl(trigger, "swatch") ||
        getDownloadBackgroundColor(trigger),
      ),
    );
    setActionEnabledState(
      downloadDropletButton,
      Boolean(
        getDownloadImageUrl(trigger, "droplet") ||
        getDownloadBackgroundColor(trigger),
      ),
    );

    modal._colourPlanetSectionRoot = sectionRoot;
  }

  function parseBooleanish(value) {
    return `${value || ""}`.trim().toLowerCase() === "true";
  }

  function normalizeColorBases(value) {
    return `${value || ""}`
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  function getSampleCatalog(sectionRoot) {
    if (!sectionRoot) {
      return [];
    }

    if (Array.isArray(sectionRoot._colourPlanetSampleCatalog)) {
      return sectionRoot._colourPlanetSampleCatalog;
    }

    const sampleCatalogScript = sectionRoot.querySelector(
      SELECTORS.sampleCatalog,
    );

    if (!sampleCatalogScript) {
      sectionRoot._colourPlanetSampleCatalog = [];
      return sectionRoot._colourPlanetSampleCatalog;
    }

    try {
      const parsedCatalog = JSON.parse(sampleCatalogScript.textContent || "[]");
      sectionRoot._colourPlanetSampleCatalog = Array.isArray(parsedCatalog)
        ? parsedCatalog
        : [];
    } catch (error) {
      console.error("[Colour Planet] Failed to parse sample catalog", error);
      sectionRoot._colourPlanetSampleCatalog = [];
    }

    return sectionRoot._colourPlanetSampleCatalog;
  }

  function isA5SwatchFreeEligible(sectionRoot) {
    return parseBooleanish(sectionRoot?.dataset.a5SwatchFreeEligible);
  }

  function getSampleActionPrice(sampleRecord, isFree) {
    if (isFree) {
      return "Free";
    }

    return `${sampleRecord?.priceDisplay || ""}`.trim();
  }

  function findMatchingSampleRecord(sampleCatalog, parentSku, colorBases) {
    if (!Array.isArray(sampleCatalog) || sampleCatalog.length === 0) {
      return null;
    }

    return (
      sampleCatalog.find((record) => {
        if (!record || record.parentSku !== parentSku) {
          return false;
        }

        const optionValues = Array.isArray(record.optionValues)
          ? record.optionValues.map((value) => `${value || ""}`.trim())
          : [];

        return colorBases.some((colorBase) => optionValues.includes(colorBase));
      }) || null
    );
  }

  function setSampleFeedback(modal, message = "", isError = false) {
    const feedback = modal?.querySelector(SELECTORS.sharedModalSampleFeedback);

    if (!feedback) {
      return;
    }

    feedback.textContent = message;
    feedback.classList.toggle("is-error", isError);
  }

  function renderSampleActionContent(button, config = {}) {
    if (!button) {
      return;
    }

    const { label = "", price = "" } = config;

    const labelElement = button.querySelector("[data-modal-button-label]");
    const priceElement = button.querySelector("[data-modal-button-price]");
    const normalizedLabel = `${label || ""}`.trim();
    const normalizedPrice = `${price || ""}`.trim();

    if (!labelElement) {
      button.textContent = normalizedLabel;
      return;
    }

    labelElement.textContent = normalizedLabel;

    if (!priceElement) {
      return;
    }

    priceElement.textContent = normalizedPrice;
    priceElement.dataset.empty = normalizedPrice ? "false" : "true";
  }

  function getSampleActionSnapshot(button) {
    if (!button) {
      return {
        label: "",
        price: "",
        disabled: true,
        isUnavailable: false,
      };
    }

    return {
      label: `${
        button.querySelector("[data-modal-button-label]")?.textContent ||
        button.dataset.defaultText ||
        button.textContent ||
        ""
      }`.trim(),
      price: `${
        button.querySelector("[data-modal-button-price]")?.textContent || ""
      }`.trim(),
      disabled: button.disabled,
      isUnavailable: button.classList.contains("is-unavailable"),
    };
  }

  function restoreSampleActionSnapshot(button, snapshot) {
    if (!button || !snapshot) {
      return;
    }

    renderSampleActionContent(button, {
      label: snapshot.label,
      price: snapshot.price,
    });
    button.disabled = Boolean(snapshot.disabled);
    button.setAttribute("aria-disabled", snapshot.disabled ? "true" : "false");
    button.classList.toggle("is-unavailable", Boolean(snapshot.isUnavailable));
  }

  function setSampleActionState(button, note, config) {
    if (!button) {
      return;
    }

    const {
      label,
      enabled,
      message = "",
      sampleRecord = null,
      parentSku = "",
      sampleType = "sample",
      price = "",
    } = config;

    renderSampleActionContent(button, { label, price });
    button.dataset.defaultText = label;
    button.dataset.sampleType = sampleType;
    button.dataset.parentSku = parentSku;

    if (enabled && sampleRecord) {
      button.dataset.sampleVariantId = `${sampleRecord.variantId || ""}`;
      button.dataset.sampleProductId = `${sampleRecord.productId || ""}`;
      button.dataset.sampleSku = `${sampleRecord.sku || ""}`;
      button.dataset.sampleFulfillmentType = `${sampleRecord.fulfillmentType || ""}`;
      button.dataset.sampleSubBrand = `${sampleRecord.subBrand || ""}`;
      button.disabled = false;
      button.setAttribute("aria-disabled", "false");
      button.classList.remove("is-unavailable");
    } else {
      delete button.dataset.sampleVariantId;
      delete button.dataset.sampleProductId;
      delete button.dataset.sampleSku;
      delete button.dataset.sampleFulfillmentType;
      delete button.dataset.sampleSubBrand;
      button.disabled = true;
      button.setAttribute("aria-disabled", "true");
      button.classList.add("is-unavailable");
    }

    if (note) {
      note.textContent = message;
      note.hidden = !message;
    }
  }

  function buildUnavailableMessage(sampleLabel) {
    return `${sampleLabel} is unavailable for this colour. Contact us to enquire about a sample.`;
  }

  function hydrateSampleActions(sectionRoot, modal, trigger) {
    if (!modal || !trigger) {
      return;
    }

    const sampleCatalog = getSampleCatalog(sectionRoot);
    const colorBases = normalizeColorBases(trigger.dataset.productColorBase);

    const addA5SwatchButton = modal.querySelector(
      SELECTORS.sharedModalAddA5Swatch,
    );
    const addSamplePotButton = modal.querySelector(
      SELECTORS.sharedModalAddSamplePot,
    );
    const a5SwatchNote = modal.querySelector(SELECTORS.sharedModalA5SwatchNote);
    const samplePotNote = modal.querySelector(
      SELECTORS.sharedModalSamplePotNote,
    );

    const a5SwatchRecord = findMatchingSampleRecord(
      sampleCatalog,
      SAMPLE_PARENT_SKUS.a5Swatch,
      colorBases,
    );
    const samplePotRecord = findMatchingSampleRecord(
      sampleCatalog,
      SAMPLE_PARENT_SKUS.samplePot,
      colorBases,
    );

    const a5SwatchEnabled =
      parseBooleanish(trigger.dataset.productHasA5Swatch) &&
      Boolean(a5SwatchRecord) &&
      Boolean(a5SwatchRecord.available);
    const samplePotEnabled =
      parseBooleanish(trigger.dataset.productHasSamplePot) &&
      Boolean(samplePotRecord) &&
      Boolean(samplePotRecord.available);
    const a5SwatchFreeEligible = isA5SwatchFreeEligible(sectionRoot);

    setSampleActionState(addA5SwatchButton, a5SwatchNote, {
      label: "Add A5 Swatch",
      enabled: a5SwatchEnabled,
      message: a5SwatchEnabled ? "" : buildUnavailableMessage("A5 swatch"),
      sampleRecord: a5SwatchRecord,
      parentSku: SAMPLE_PARENT_SKUS.a5Swatch,
      sampleType: "A5 swatch",
      price: a5SwatchEnabled
        ? getSampleActionPrice(
            a5SwatchRecord,
            a5SwatchEnabled && a5SwatchFreeEligible,
          )
        : "",
    });

    setSampleActionState(addSamplePotButton, samplePotNote, {
      label: "Add Sample Pot",
      enabled: samplePotEnabled,
      message: samplePotEnabled ? "" : buildUnavailableMessage("Sample pot"),
      sampleRecord: samplePotRecord,
      parentSku: SAMPLE_PARENT_SKUS.samplePot,
      sampleType: "sample pot",
      price: samplePotEnabled
        ? getSampleActionPrice(samplePotRecord, false)
        : "",
    });

    modal._colourPlanetSectionRoot = sectionRoot;
    modal._colourPlanetCurrentTrigger = trigger;
    modal._colourPlanetCurrentProduct = {
      title: (trigger.dataset.productTitle || "").trim(),
      url: (trigger.dataset.productUrl || "").trim(),
      sku: (trigger.dataset.productOriginalSku || "").trim(),
      fulfillmentType: (trigger.dataset.productFulfillmentType || "").trim(),
      subBrand: (trigger.dataset.productSubBrand || "").trim(),
    };

    setSampleFeedback(modal, "");
  }

  function buildOriginalProductUrl(productUrl) {
    const normalizedProductUrl = `${productUrl || ""}`.trim();

    if (!normalizedProductUrl) {
      return `${window.location.origin}${window.location.pathname}`;
    }

    try {
      return new URL(normalizedProductUrl, window.location.origin).toString();
    } catch (error) {
      return `${window.location.origin}${window.location.pathname}`;
    }
  }

  async function refreshCartDrawer() {
    const cartDrawer = document.querySelector("cart-drawer");

    if (!cartDrawer) {
      return;
    }

    try {
      const response = await fetch(
        `${window.routes?.cart_url || "/cart"}?section_id=cart-drawer`,
      );
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, "text/html");
      const newCartDrawer = html.querySelector("cart-drawer");

      if (newCartDrawer) {
        cartDrawer.innerHTML = newCartDrawer.innerHTML;
        cartDrawer.classList.remove("is-empty");

        const cartDrawerItems = cartDrawer.querySelector("cart-drawer-items");
        if (cartDrawerItems) {
          cartDrawerItems.classList.remove("is-empty");
        }
      }

      if (typeof cartDrawer.open === "function") {
        cartDrawer.open();
      }
    } catch (error) {
      console.error("[Colour Planet] Error refreshing cart drawer", error);
      if (typeof cartDrawer.open === "function") {
        cartDrawer.open();
      }
    }
  }

  async function refreshHeaderCartCount() {
    try {
      const response = await fetch(
        `${window.routes?.cart_url || "/cart"}?section_id=header`,
      );
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, "text/html");

      const cartIconBubble = document.querySelector("#cart-icon-bubble");
      const newCartIconBubble = html.querySelector("#cart-icon-bubble");

      if (cartIconBubble && newCartIconBubble) {
        cartIconBubble.replaceWith(newCartIconBubble);
      }
    } catch (error) {
      console.error(
        "[Colour Planet] Error refreshing header cart count",
        error,
      );
    }
  }

  async function addSampleProductToCart(modal, button) {
    if (!modal || !button || modal.dataset.sampleLoading === "true") {
      return;
    }

    const sampleVariantId = `${button.dataset.sampleVariantId || ""}`.trim();

    if (!sampleVariantId) {
      return;
    }

    const originalActionSnapshot = getSampleActionSnapshot(button);
    const cart =
      document.querySelector("cart-notification") ||
      document.querySelector("cart-drawer");
    let feedbackMessage = "";
    let feedbackIsError = false;

    renderSampleActionContent(button, { label: "Adding..." });
    button.disabled = true;
    button.setAttribute("aria-disabled", "true");
    modal.dataset.sampleLoading = "true";
    setSampleFeedback(modal, "");

    try {
      const formData = new FormData();
      formData.append("id", sampleVariantId);
      formData.append("quantity", "1");

      const currentProduct = modal._colourPlanetCurrentProduct || {};

      if (currentProduct.title) {
        formData.append(
          "properties[_Original Product Name]",
          currentProduct.title,
        );
      }

      formData.append(
        "properties[_Original Product URL]",
        buildOriginalProductUrl(currentProduct.url),
      );

      if (currentProduct.sku) {
        formData.append(
          "properties[_Original Product SKU]",
          currentProduct.sku,
        );
      }

      formData.append(
        "properties[_product_fullfillment_type]",
        button.dataset.sampleFulfillmentType || "",
      );

      formData.append(
        "properties[_product_sub_brand]",
        button.dataset.sampleSubBrand || "",
      );

      const config = fetchConfig("javascript");

      config.headers = config.headers || {};
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];

      if (cart && typeof cart.getSectionsToRender === "function") {
        formData.append(
          "sections",
          cart.getSectionsToRender().map((section) => section.id),
        );
        formData.append("sections_url", window.location.pathname);

        if (typeof cart.setActiveElement === "function") {
          cart.setActiveElement(button);
        }
      }

      config.body = formData;

      const response = await fetch(
        window.routes?.cart_add_url || "/cart/add.js",
        config,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status && data.status !== "success") {
        throw new Error(data.message || "Failed to add product to cart");
      }

      if (
        typeof publish === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "product-form",
          cartData: data,
        });
      }

      if (cart && typeof cart.renderContents === "function" && data.sections) {
        cart.renderContents(data);
      } else {
        await Promise.all([refreshCartDrawer(), refreshHeaderCartCount()]);
      }

      feedbackMessage = `${button.dataset.sampleType || "Sample"} added to cart.`;
    } catch (error) {
      console.error(
        "[Colour Planet] Error adding sample product to cart",
        error,
      );
      feedbackMessage = `Unable to add ${button.dataset.sampleType || "sample"} right now.`;
      feedbackIsError = true;
    } finally {
      modal.dataset.sampleLoading = "false";
      restoreSampleActionSnapshot(button, originalActionSnapshot);

      const currentTrigger = modal._colourPlanetCurrentTrigger;
      const currentSectionRoot = modal._colourPlanetSectionRoot;

      if (currentTrigger && currentSectionRoot) {
        try {
          hydrateSampleActions(currentSectionRoot, modal, currentTrigger);
        } catch (error) {
          console.error(
            "[Colour Planet] Error restoring sample action state",
            error,
          );
        }
      }

      if (feedbackMessage) {
        setSampleFeedback(modal, feedbackMessage, feedbackIsError);
      }
    }
  }

  function initializeModalSampleActions(modal) {
    if (!modal || modal.dataset.sampleActionsInitialized === "true") {
      return;
    }

    modal.dataset.sampleActionsInitialized = "true";

    [
      modal.querySelector(SELECTORS.sharedModalAddA5Swatch),
      modal.querySelector(SELECTORS.sharedModalAddSamplePot),
    ]
      .filter(Boolean)
      .forEach((button) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();
          if (button.disabled) {
            return;
          }

          addSampleProductToCart(modal, button);
        });
      });
  }

  function initializeModalDownloadActions(modal) {
    if (!modal || modal.dataset.downloadActionsInitialized === "true") {
      return;
    }

    modal.dataset.downloadActionsInitialized = "true";

    [
      {
        button: modal.querySelector(SELECTORS.sharedModalDownloadSwatch),
        type: "swatch",
      },
      {
        button: modal.querySelector(SELECTORS.sharedModalDownloadDroplet),
        type: "droplet",
      },
    ].forEach(({ button, type }) => {
      if (!button) {
        return;
      }

      button.addEventListener("click", async (event) => {
        event.preventDefault();

        if (button.disabled || modal.dataset.downloadLoading === "true") {
          return;
        }

        const trigger = modal._colourPlanetCurrentTrigger;
        const sectionRoot = modal._colourPlanetSectionRoot;

        if (!trigger || !sectionRoot) {
          return;
        }

        const defaultText = button.textContent;
        button.textContent = "Preparing...";
        modal.dataset.downloadLoading = "true";

        try {
          await triggerColourPlanetDownload(sectionRoot, trigger, type);
        } catch (error) {
          console.error(
            `[Colour Planet] Failed to download ${type} asset`,
            error,
          );
        } finally {
          modal.dataset.downloadLoading = "false";
          button.textContent = defaultText;
        }
      });
    });
  }

  function updateFilterButtonState(button, isActive) {
    button.classList.toggle("is-active", isActive);
    button.setAttribute("aria-pressed", isActive ? "true" : "false");
  }

  function formatAvailableCount(count, filtered) {
    const normalizedCount = Number.parseInt(`${count || "0"}`, 10);

    if (Number.isNaN(normalizedCount)) {
      return filtered
        ? "0 colours available (filtered)"
        : "0 colours available";
    }

    return filtered
      ? `${normalizedCount} colours available (filtered)`
      : `${normalizedCount} colours available`;
  }

  function updateAvailableCount(sectionRoot, activeFilters, allIsActive) {
    const countElement = sectionRoot.querySelector(SELECTORS.availableCount);

    if (!countElement) {
      return;
    }

    let nextText = "";

    if (allIsActive) {
      nextText = formatAvailableCount(
        sectionRoot.dataset.totalColourCount,
        false,
      );
    } else {
      const groupElements = Array.from(
        sectionRoot.querySelectorAll(SELECTORS.group),
      );
      const filteredCount = groupElements.reduce((sum, group) => {
        const groupFilter = group.dataset.filterGroup || "";

        if (!activeFilters.has(groupFilter)) {
          return sum;
        }

        return (
          sum + Number.parseInt(group.dataset.groupProductCount || "0", 10)
        );
      }, 0);

      nextText = formatAvailableCount(filteredCount, true);
    }

    if (countElement.textContent !== nextText) {
      countElement.textContent = nextText;
    }
  }

  function applyFilterState(sectionRoot) {
    const filterButtons = Array.from(
      sectionRoot.querySelectorAll(SELECTORS.filterButton),
    );
    const groupElements = Array.from(
      sectionRoot.querySelectorAll(SELECTORS.group),
    );
    const activeFilters = sectionRoot._colourPlanetActiveFilters || new Set();
    const allIsActive = activeFilters.size === 0;

    filterButtons.forEach((button) => {
      const filterValue = button.dataset.filter || "";
      const isActive =
        filterValue === "all" ? allIsActive : activeFilters.has(filterValue);

      updateFilterButtonState(button, isActive);
    });

    groupElements.forEach((group) => {
      const groupFilter = group.dataset.filterGroup || "";
      const isVisible = allIsActive || activeFilters.has(groupFilter);

      group.classList.toggle("is-hidden", !isVisible);
      group.hidden = !isVisible;
      group.setAttribute("aria-hidden", isVisible ? "false" : "true");
    });

    updateAvailableCount(sectionRoot, activeFilters, allIsActive);
  }

  function handleFilterClick(sectionRoot, clickedButton) {
    const filterValue = clickedButton.dataset.filter || "all";
    const activeFilters = new Set(sectionRoot._colourPlanetActiveFilters || []);

    if (filterValue === "all") {
      activeFilters.clear();
    } else if (activeFilters.has(filterValue)) {
      activeFilters.delete(filterValue);
    } else {
      activeFilters.add(filterValue);
    }

    sectionRoot._colourPlanetActiveFilters = activeFilters;
    applyFilterState(sectionRoot);
  }

  function initializeFilters(root) {
    const sectionRoots = [];

    if (root instanceof Element && root.matches(SELECTORS.sectionRoot)) {
      sectionRoots.push(root);
    }

    if (root.querySelectorAll) {
      sectionRoots.push(...root.querySelectorAll(SELECTORS.sectionRoot));
    }

    sectionRoots.forEach((sectionRoot) => {
      const filters = sectionRoot.querySelector(SELECTORS.filters);
      if (!filters) {
        return;
      }

      if (sectionRoot.dataset.filtersInitialized !== "true") {
        sectionRoot.dataset.filtersInitialized = "true";
        filters.addEventListener("click", (event) => {
          const clickedButton = event.target.closest(SELECTORS.filterButton);
          if (!clickedButton || !filters.contains(clickedButton)) {
            return;
          }

          handleFilterClick(sectionRoot, clickedButton);
        });
      }

      sectionRoot._colourPlanetActiveFilters = new Set();
      applyFilterState(sectionRoot);
    });
  }

  function initializeSharedModal(root) {
    const sectionRoots = [];

    if (root instanceof Element && root.matches(SELECTORS.sectionRoot)) {
      sectionRoots.push(root);
    }

    if (root.querySelectorAll) {
      sectionRoots.push(...root.querySelectorAll(SELECTORS.sectionRoot));
    }

    sectionRoots.forEach((sectionRoot) => {
      if (sectionRoot.dataset.modalInitialized === "true") {
        return;
      }

      sectionRoot.dataset.modalInitialized = "true";

      sectionRoot
        .querySelectorAll(SELECTORS.cardButton)
        .forEach((trigger) => setTriggerExpandedState(trigger, false));

      const sharedModal = document.querySelector(
        `#ColourPlanetModal-${sectionRoot.id.replace("ColourPlanet-", "")}`,
      );

      if (
        sharedModal &&
        sharedModal.dataset.colourPlanetStateObserverInitialized !== "true"
      ) {
        sharedModal.dataset.colourPlanetStateObserverInitialized = "true";

        const syncExpandedState = () => {
          const isOpen = sharedModal.hasAttribute("open");
          setTriggerExpandedState(
            sharedModal._colourPlanetCurrentTrigger,
            isOpen,
          );

          if (!isOpen) {
            sharedModal._colourPlanetCurrentTrigger = null;
          }
        };

        const stateObserver = new MutationObserver(syncExpandedState);
        stateObserver.observe(sharedModal, {
          attributes: true,
          attributeFilter: ["open"],
        });
        syncExpandedState();
      }

      sectionRoot.addEventListener("keydown", (event) => {
        const trigger = event.target.closest(SELECTORS.cardButton);
        if (!trigger || !sectionRoot.contains(trigger)) {
          return;
        }

        if (event.key === "Enter") {
          trigger.dataset.colourPlanetInitialFocus = "close-button";
        }
      });

      sectionRoot.addEventListener("click", (event) => {
        const unavailableAction = event.target.closest(
          '.colour-planet__modal-button[aria-disabled="true"]',
        );

        if (unavailableAction && sectionRoot.contains(unavailableAction)) {
          event.preventDefault();
          return;
        }

        const trigger = event.target.closest(SELECTORS.cardButton);
        if (!trigger || !sectionRoot.contains(trigger)) {
          return;
        }

        const focusCloseButton = shouldFocusCloseButton(trigger);

        const modalSelector = trigger.dataset.modal;
        const modal = modalSelector
          ? document.querySelector(modalSelector)
          : null;
        if (!modal) {
          return;
        }

        initializeModalSampleActions(modal);
        initializeModalDownloadActions(modal);

        const modalTitle = modal.querySelector(SELECTORS.sharedModalTitle);
        const modalSave = modal.querySelector(SELECTORS.sharedModalSave);
        const modalDescription = modal.querySelector(
          SELECTORS.sharedModalDescription,
        );
        const modalCode = modal.querySelector(SELECTORS.sharedModalCode);
        const modalFandeck = modal.querySelector(SELECTORS.sharedModalFandeck);
        const modalCoats = modal.querySelector(SELECTORS.sharedModalCoats);
        const modalUndercoat = modal.querySelector(
          SELECTORS.sharedModalUndercoat,
        );
        const modalRgb = modal.querySelector(SELECTORS.sharedModalRgb);
        const modalHex = modal.querySelector(SELECTORS.sharedModalHex);
        const modalLrv = modal.querySelector(SELECTORS.sharedModalLrv);
        const modalCollection = modal.querySelector(
          SELECTORS.sharedModalCollection,
        );
        const modalDownloadBim = modal.querySelector(
          SELECTORS.sharedModalDownloadBim,
        );
        const modalDownloadCollectionBim = modal.querySelector(
          SELECTORS.sharedModalDownloadCollectionBim,
        );
        const modalDownloadFullBim = modal.querySelector(
          SELECTORS.sharedModalDownloadFullBim,
        );
        const modalViewColour = modal.querySelector(
          SELECTORS.sharedModalViewColour,
        );
        const modalColourStudio = modal.querySelector(
          SELECTORS.sharedModalColourStudio,
        );
        const modalCreatePalette = modal.querySelector(
          SELECTORS.sharedModalCreatePalette,
        );
        const modalHero = modal.querySelector(SELECTORS.sharedModalHero);

        const productTitle = (trigger.dataset.productTitle || "").trim();
        const productSku = (trigger.dataset.productOriginalSku || "").trim();

        setModalTextContent(
          modalTitle,
          trigger.dataset.productTitle,
          "Untitled colour",
        );
        hydrateModalSaveButton(modalSave, trigger);
        setOptionalModalTextContent(
          modalDescription,
          trigger.dataset.productDescription,
        );
        setModalTextContent(modalCode, trigger.dataset.productCode);
        setModalTextContent(modalFandeck, trigger.dataset.productFandeck);
        setOptionalModalTextContent(modalCoats, trigger.dataset.productCoats);
        setOptionalModalTextContent(
          modalUndercoat,
          trigger.dataset.productUndercoat,
        );
        setModalTextContent(modalRgb, trigger.dataset.productRgb);
        setModalTextContent(modalHex, trigger.dataset.productHex);
        setModalTextContent(modalLrv, trigger.dataset.productLrv);
        setModalTextContent(modalCollection, trigger.dataset.productCollection);

        if (modalDownloadBim) {
          modalDownloadBim.textContent = productTitle
            ? `Download BIM ${productTitle}`
            : "Download BIM";
          setModalLinkAttributes(
            modalDownloadBim,
            buildColourBimUrl(productTitle, sectionRoot),
          );
        }

        setModalLinkAttributes(
          modalDownloadCollectionBim,
          (
            trigger.dataset.productCollectionBimUrl ||
            trigger.closest(SELECTORS.group)?.dataset.collectionBimUrl ||
            ""
          ).trim(),
        );

        setModalLinkAttributes(
          modalDownloadFullBim,
          buildBimUrl(getFullBimItemId(sectionRoot), sectionRoot),
        );

        setModalLinkAttributes(
          modalViewColour,
          (trigger.dataset.productUrl || "").trim() || "#",
        );

        setOptionalExternalModalLinkAttributes(
          modalColourStudio,
          buildColourStudioUrl(productTitle, productSku, sectionRoot),
        );

        setOptionalExternalModalLinkAttributes(
          modalCreatePalette,
          buildCreatePaletteUrl(productTitle, productSku, sectionRoot),
        );

        if (modalHero) {
          modalHero.style.setProperty(
            "--colour-planet-modal-swatch",
            trigger.dataset.productSwatch || "#ece6d7",
          );
          modalHero.classList.remove("is-dark-text", "is-light-text");
          modalHero.classList.add(
            trigger.dataset.productContrastClass || "is-dark-text",
          );
        }

        hydrateSampleActions(sectionRoot, modal, trigger);
        hydrateDownloadActions(sectionRoot, modal, trigger);
        modal._colourPlanetCurrentTrigger = trigger;
        setTriggerExpandedState(trigger, true);

        modal.show(trigger);

        if (focusCloseButton) {
          const closeButton = modal.querySelector('[id^="ModalClose-"]');
          if (closeButton) {
            requestAnimationFrame(() => {
              closeButton.focus();
            });
          }
        }
      });
    });
  }

  function setLoaderLabel(control, message) {
    const label = control.querySelector(SELECTORS.loaderLabel);
    if (label) {
      label.textContent = message;
    }
  }

  function setManualMode(control) {
    control.dataset.triggerMode = "manual";
    control.classList.add("is-manual-only");
  }

  function clearManualMode(control) {
    if (control.dataset.triggerMode === "auto") {
      control.classList.remove("is-manual-only");
    }
  }

  function setLoaderError(control, message) {
    control.classList.add("has-error");
    setManualMode(control);
    setLoaderLabel(control, message);
  }

  function clearLoaderError(control) {
    control.classList.remove("has-error");
    clearManualMode(control);
  }

  function getRequestUrl(control) {
    const url = new URL(window.location.href);
    url.hash = "";
    url.searchParams.delete("page");
    url.searchParams.delete("section_id");
    url.searchParams.set("section_id", control.dataset.sectionInstanceId || "");
    url.searchParams.set("page", control.dataset.nextPage || "1");
    return url;
  }

  async function fetchNextPageMarkup(control) {
    const response = await fetch(getRequestUrl(control).toString(), {
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch page ${control.dataset.nextPage || "1"}`,
      );
    }

    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, "text/html");
    const group = html.querySelector(
      `.colour-planet__group[data-block-position="${control.dataset.blockPosition}"]`,
    );

    if (!group) {
      throw new Error("Target group was missing from the section response");
    }

    const cardNodes = Array.from(
      group.querySelectorAll(
        ".colour-planet__card-item:not(.colour-planet__card-item--loader)",
      ),
    ).map((node) => document.importNode(node, true));
    const nextControl = group.querySelector(SELECTORS.control);

    return {
      cardNodes,
      hasNextPage: Boolean(
        nextControl && nextControl.dataset.hasNextPage === "true",
      ),
      nextPage: nextControl?.dataset.nextPage || "",
      loadedCount: nextControl?.dataset.loadedCount || `${cardNodes.length}`,
    };
  }

  function createRowElement(rowNumber) {
    const row = document.createElement("section");
    row.className = "colour-planet__group-row";
    row.dataset.rowNumber = `${rowNumber}`;
    row.setAttribute("aria-label", `Leaf ${rowNumber}`);

    const label = document.createElement("p");
    label.className = "colour-planet__row-label";
    label.textContent = `Leaf ${rowNumber}`;

    const list = document.createElement("ul");
    list.className = "colour-planet__cards";
    list.setAttribute("role", "list");

    row.appendChild(label);
    row.appendChild(list);

    return row;
  }

  function getLastRow(rowsContainer) {
    const rows = rowsContainer.querySelectorAll(SELECTORS.groupRow);
    return rows[rows.length - 1] || null;
  }

  function isAutoTriggerInRange(control) {
    const sentinel = control?.querySelector(SELECTORS.sentinel);

    if (!sentinel) {
      return false;
    }

    const { top, bottom } = sentinel.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || 0;

    return (
      bottom >= -AUTO_APPEND_ROOT_MARGIN_PX &&
      top <= viewportHeight + AUTO_APPEND_ROOT_MARGIN_PX
    );
  }

  function queueAutoAppendIfNeeded(control) {
    window.requestAnimationFrame(() => {
      if (
        !control?.isConnected ||
        control.dataset.loading === "true" ||
        control.dataset.triggerMode !== "auto" ||
        control.dataset.hasNextPage !== "true" ||
        !isAutoTriggerInRange(control)
      ) {
        return;
      }

      appendNextRows(control);
    });
  }

  function appendCardsIntoRows(rowsContainer, cardsToAppend, loaderItem) {
    let currentRow = getLastRow(rowsContainer);

    if (!currentRow) {
      const leafOffset = Number.parseInt(
        rowsContainer.dataset.leafOffset || "0",
        10,
      );
      currentRow = createRowElement(leafOffset + 1);
      rowsContainer.appendChild(currentRow);
    }

    if (loaderItem) {
      loaderItem.remove();
    }

    let currentList = currentRow.querySelector(SELECTORS.cardsList);
    let currentRowNumber = Number.parseInt(
      currentRow.dataset.rowNumber || "1",
      10,
    );

    cardsToAppend.forEach((cardNode) => {
      const existingCards = currentList.querySelectorAll(
        ".colour-planet__card-item:not(.colour-planet__card-item--loader)",
      ).length;

      if (existingCards >= 8) {
        currentRowNumber += 1;
        currentRow = createRowElement(currentRowNumber);
        rowsContainer.appendChild(currentRow);
        currentList = currentRow.querySelector(SELECTORS.cardsList);
      }

      currentList.appendChild(cardNode);
    });

    if (loaderItem) {
      currentList.appendChild(loaderItem);
    }
  }

  async function appendNextRows(control) {
    let shouldContinueAutoAppend = false;

    if (
      !control ||
      control.dataset.loading === "true" ||
      control.dataset.hasNextPage !== "true"
    ) {
      return;
    }

    const group = control.closest(".colour-planet__group");
    const rowsContainer = group?.querySelector(SELECTORS.groupRows);
    const loaderItem = control.closest(SELECTORS.loaderItem);

    if (!rowsContainer || !loaderItem) {
      return;
    }

    control.dataset.loading = "true";
    control.classList.add("is-loading");
    clearLoaderError(control);
    setLoaderLabel(control, "Loading more swatches...");

    try {
      const { cardNodes, hasNextPage, nextPage, loadedCount } =
        await fetchNextPageMarkup(control);

      if (cardNodes.length === 0) {
        control.dataset.hasNextPage = "false";
        loaderItem.remove();
        return;
      }

      appendCardsIntoRows(
        rowsContainer,
        cardNodes,
        hasNextPage ? loaderItem : null,
      );

      control.dataset.loadedCount = loadedCount;
      control.dataset.hasNextPage = hasNextPage ? "true" : "false";
      if (nextPage) {
        control.dataset.nextPage = nextPage;
      }

      if (!hasNextPage) {
        loaderItem.remove();
        return;
      }

      if (control.dataset.triggerMode === "auto") {
        registerAutoTrigger(control);

        const autoAppendedCount =
          Number.parseInt(control.dataset.autoAppendedCount || "0", 10) + 1;
        control.dataset.autoAppendedCount = `${autoAppendedCount}`;

        if (autoAppendedCount >= AUTO_APPEND_LIMIT) {
          setManualMode(control);
          setLoaderLabel(control, "More swatches available");
        } else {
          shouldContinueAutoAppend = true;
        }
      }
    } catch (error) {
      setLoaderError(control, "Load failed. Try again.");
      console.error("[Colour Planet] Failed to append swatches", error);
    } finally {
      control.dataset.loading = "false";
      control.classList.remove("is-loading");

      if (shouldContinueAutoAppend) {
        queueAutoAppendIfNeeded(control);
      }
    }
  }

  function registerManualTrigger(control) {
    const button = control.querySelector(SELECTORS.button);
    if (!button || button.dataset.initialized === "true") {
      return;
    }

    button.dataset.initialized = "true";
    button.addEventListener("click", () => {
      appendNextRows(control);
    });
  }

  function registerAutoTrigger(control) {
    const sentinel = control.querySelector(SELECTORS.sentinel);

    if (!sentinel) {
      return;
    }

    if (!("IntersectionObserver" in window)) {
      setManualMode(control);
      setLoaderLabel(control, "More swatches available");
      return;
    }

    if (control._colourPlanetObserver) {
      control._colourPlanetObserver.disconnect();
    }

    control._colourPlanetObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || control.dataset.triggerMode !== "auto") {
            return;
          }

          appendNextRows(control);
        });
      },
      {
        rootMargin: `${AUTO_APPEND_ROOT_MARGIN_PX}px 0px`,
      },
    );

    control._colourPlanetObserver.observe(sentinel);
  }

  function init(root = document) {
    initializeFilters(root);
    initializeSharedModal(root);

    const controls = root.querySelectorAll(
      `${SELECTORS.control}:not([data-initialized="true"])`,
    );

    controls.forEach((control) => {
      control.dataset.initialized = "true";
      registerManualTrigger(control);
      registerAutoTrigger(control);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document));
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    init(event?.target || document);
  });
})();
